import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Search, Loader2, X, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import LocationPicker from "./calendar/LocationPicker";

/**
 * LocationPermissionPrompt — first-time-login gentle GPS request.
 *
 * Shown exactly once per user, the first time they land in the app
 * with a logged-in session and no explicit preferred location yet.
 * Purpose: auto-feed accurate coordinates to the EXISTING zmanim /
 * candle-lighting / parasha engine so the calendar and dashboard
 * surface times specific to where they actually are — not Jerusalem
 * by default for everyone.
 *
 * What it does NOT touch:
 *   • Zmanim calculation logic (services/zmanim, timezonefinder, etc.)
 *   • The existing LocationPicker — we *reuse* it for manual fallback.
 *   • `/me/location` PUT route — we call the same helper as the picker.
 *   • Reverse-geocode logic — same `api.reverseGeocode` path the picker
 *     already uses on its GPS button.
 *
 * Dismissal is persistent — once the user picks any outcome (allow,
 * manual, "later"), we set `torahya_location_prompted=1` in
 * localStorage so they never see this prompt again. They can always
 * change their location from the calendar's existing picker.
 */
const LS_PROMPTED = "torahya_location_prompted";
const LS_PREF_LOC = "tanakh_pref_location"; // mirror of LocationPicker

// Routes where we MUST NOT pop the modal — auth flow, onboarding tour,
// landing page, donation redirects. Anywhere else (Beit Midrash, Library,
// Reader, Calendar...) is fair game.
const SUPPRESSED_ROUTES = [
  "/",                       // landing
  "/auth",
  "/auth/callback",
  "/auth/google",
  "/onboarding",
  "/donate/thank-you",
  "/__gematria-preview",
];

function isSuppressed(pathname) {
  return SUPPRESSED_ROUTES.some(
    (r) => pathname === r || pathname.startsWith(`${r}/`),
  );
}

/**
 * Has the user ever explicitly set a real location?
 * Treats:
 *   • `preferred_location` being a custom descriptor (dict)  → yes
 *   • `preferred_location` being any catalog id OTHER than the
 *     default "jerusalem"                                    → yes
 *   • localStorage descriptor with kind=="custom"            → yes
 * Otherwise → no (default jerusalem was never overridden).
 */
function hasExplicitLocation(user) {
  const pref = user?.preferred_location;
  if (pref && typeof pref === "object") return true;
  if (typeof pref === "string" && pref && pref !== "jerusalem") return true;
  try {
    const raw = localStorage.getItem(LS_PREF_LOC);
    if (raw) {
      const d = JSON.parse(raw);
      if (d && d.kind === "custom") return true;
    }
  } catch { /* ignore */ }
  return false;
}

export default function LocationPermissionPrompt() {
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const [open, setOpen] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Decide whether to show the prompt on each user/route change.
  useEffect(() => {
    if (!user) return;
    if (isSuppressed(location.pathname)) return;
    // Don't interrupt the live tutorial — let it finish first. The
    // tutorial finishes by navigating to /library and we'll re-check
    // there; tutorial only runs when onboarding_completed === false.
    if (user.onboarding_completed === false) return;
    // Already prompted? Never again.
    try {
      if (localStorage.getItem(LS_PROMPTED) === "1") return;
    } catch { /* ignore */ }
    if (hasExplicitLocation(user)) {
      // User already has a real location — silently mark prompted so
      // we don't ever ask. Treat as "implicitly handled".
      try { localStorage.setItem(LS_PROMPTED, "1"); } catch { /* ignore */ }
      return;
    }
    // Defer slightly so the page render settles before we pop a modal.
    const t = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(t);
  }, [user, location.pathname]);

  const markPrompted = useCallback(() => {
    try { localStorage.setItem(LS_PROMPTED, "1"); } catch { /* ignore */ }
  }, []);

  const persistDescriptor = useCallback(async (descriptor) => {
    // Same persistence path as LocationPicker.choose() — single source
    // of truth so the calendar/dashboard pick it up identically.
    try { localStorage.setItem(LS_PREF_LOC, JSON.stringify(descriptor)); } catch { /* ignore */ }
    try { await api.setMyLocation(descriptor); } catch { /* ignore */ }
  }, []);

  const handleAllowGps = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error(t("location_prompt.toast_no_geo_title"), {
        description: t("location_prompt.toast_no_geo_desc"),
      });
      setPickerOpen(true);
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const rev = await api.reverseGeocode(latitude, longitude);
          const descriptor = {
            kind: "custom",
            latitude,
            longitude,
            tzid: rev.tzid,
            name: rev.name || rev.city || t("location_prompt.toast_default_name"),
            name_en: rev.name_en || rev.city,
            display_name: rev.display_name,
            country: rev.country,
            cc: rev.cc,
          };
          await persistDescriptor(descriptor);
          markPrompted();
          // Pick the locale-appropriate display name so the toast
          // doesn't accidentally show "ירושלים" inside an EN session
          // when the geocoder returned both names.
          const displayName = isEn
            ? (descriptor.name_en || descriptor.name)
            : (descriptor.name || descriptor.name_en);
          toast.success(t("location_prompt.toast_detected"), {
            description: displayName,
          });
          setOpen(false);
        } catch (err) {
          toast.error(t("location_prompt.toast_failed_title"), {
            description: err?.response?.data?.detail || t("location_prompt.toast_failed_desc"),
          });
          // Stay open so the user can fall back to manual or "later".
        } finally {
          setGpsLoading(false);
        }
      },
      (err) => {
        setGpsLoading(false);
        if (err?.code === 1) {
          // User denied — graceful fallback to manual picker.
          toast.message(t("location_prompt.toast_denied_title"), {
            description: t("location_prompt.toast_denied_desc"),
          });
          setPickerOpen(true);
        } else {
          toast.error(t("location_prompt.toast_geo_err_title"), {
            description: t("location_prompt.toast_geo_err_desc"),
          });
          setPickerOpen(true);
        }
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  }, [persistDescriptor, markPrompted, t, isEn]);

  const handleManual = () => setPickerOpen(true);

  const handleLater = () => {
    markPrompted();
    setOpen(false);
  };

  const handlePickerPicked = () => {
    // LocationPicker.choose() already persisted both locally and to
    // the BE; we just need to close ourselves and mark prompted.
    markPrompted();
    setPickerOpen(false);
    setOpen(false);
  };

  if (!user || (!open && !pickerOpen)) return null;

  // The manual picker takes over when opened — we hide our own modal
  // beneath it so we don't render two stacked dialogs.
  if (pickerOpen) {
    return (
      <LocationPicker
        open={pickerOpen}
        onClose={handlePickerPicked}
        current={null}
        onPick={() => { /* LocationPicker handles its own persistence */ }}
        isAuthed={true}
      />
    );
  }

  return (
    <div
      data-testid="location-permission-prompt"
      className="fixed inset-0 z-[95] flex items-center justify-center px-4"
      dir={isEn ? "ltr" : "rtl"}
      role="dialog"
      aria-modal="true"
      aria-labelledby="location-prompt-title"
    >
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
        onClick={handleLater}
        aria-hidden="true"
      />
      <div className="relative z-10 tutorial-card max-w-md w-full px-7 py-7 sm:px-9 sm:py-8 location-prompt-card">
        {/* Close button — same affordance as the tutorial overlay */}
        <button
          type="button"
          onClick={handleLater}
          aria-label={t("location_prompt.close_aria")}
          data-testid="location-prompt-close"
          className={`absolute top-3 ${isEn ? "right-3" : "left-3"} text-muted-foreground hover:text-foreground p-1 rounded-sm`}
        >
          <X className="h-4 w-4" strokeWidth={1.5} />
        </button>

        {/* Decorative icon — gold halo around a map pin, matching the
            sacred-app aesthetic (no purple gradients, no AI slop). */}
        <div className="flex justify-center mb-4">
          <div className="location-prompt-halo">
            <MapPin
              className="h-7 w-7 text-gold"
              strokeWidth={1.5}
              aria-hidden="true"
            />
          </div>
        </div>

        <h2
          id="location-prompt-title"
          data-testid="location-prompt-title"
          className="font-heading text-2xl sm:text-3xl text-foreground text-center"
        >
          {t("location_prompt.title")}
        </h2>
        <p
          data-testid="location-prompt-body"
          className="text-center text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed"
        >
          {t("location_prompt.body_lead")}{" "}
          <span className="text-foreground">{t("location_prompt.body_zmanim")}</span>
          {", "}
          <span className="text-foreground">{t("location_prompt.body_candles")}</span>
          {isEn ? " & " : " ו"}
          <span className="text-foreground">{t("location_prompt.body_parasha")}</span>
          {" "}
          {t("location_prompt.body_tail")}
        </p>

        <div className="flex items-start gap-2 mt-4 mx-auto max-w-sm text-xs sm:text-sm text-muted-foreground bg-gold/5 border border-gold/20 rounded-sm px-3 py-2">
          <ShieldCheck
            className="h-4 w-4 text-gold flex-shrink-0 mt-0.5"
            strokeWidth={1.5}
            aria-hidden="true"
          />
          <span>
            {t("location_prompt.privacy_note")}
          </span>
        </div>

        <div className="flex flex-col gap-2 mt-6">
          <button
            type="button"
            onClick={handleAllowGps}
            disabled={gpsLoading}
            data-testid="location-prompt-allow"
            className="sacred-btn-gold inline-flex items-center justify-center gap-2 py-3 px-6 rounded-sm font-heading disabled:opacity-60"
          >
            {gpsLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                {t("location_prompt.btn_locating")}
              </>
            ) : (
              <>
                <MapPin className="h-4 w-4" strokeWidth={1.75} />
                {t("location_prompt.btn_allow")}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleManual}
            disabled={gpsLoading}
            data-testid="location-prompt-manual"
            className="inline-flex items-center justify-center gap-2 py-2.5 px-6 rounded-sm font-heading text-sm text-foreground border border-foreground/15 hover:bg-foreground/5"
          >
            <Search className="h-4 w-4" strokeWidth={1.5} />
            {t("location_prompt.btn_manual")}
          </button>
          <button
            type="button"
            onClick={handleLater}
            disabled={gpsLoading}
            data-testid="location-prompt-later"
            className="text-xs sm:text-sm text-muted-foreground hover:text-foreground py-2"
          >
            {t("location_prompt.btn_later")}
          </button>
        </div>
      </div>
    </div>
  );
}
